import { ProdClient } from 'client/client';
import { getTranslations } from 'helpers/translations';
import { distinctLangaugeVariants } from 'lib/locales/languages';
import { forEach } from 'lodash';
import { ContentPage, Homepage } from 'models';
import { normalize } from 'pages/api/sitemap';

const DEPTH_PARAMETER = 3;
const TEAM_DEPTH_PARAMETER = 4;

const containsTeamModule = (value: unknown, visited = new WeakSet<object>()): boolean => {
  if (!value || typeof value !== 'object') return false;

  const ref = value as object;
  if (visited.has(ref)) return false;
  visited.add(ref);

  if (Array.isArray(value)) return value.some((item) => containsTeamModule(item, visited));

  const item = value as { system?: { type?: string }; elements?: Record<string, unknown> };
  if (item.system?.type === 'team') return true;
  return Object.values(item as Record<string, unknown>).some((child) => containsTeamModule(child, visited));
};

export type SitemapDataWithCampaigns = {
  campaignUrl: string;
  urls: ReturnType<typeof normalize>[];
}[];

export type SitemapData = ReturnType<typeof normalize>[];

export class SitemapService {
  private _linkedItems: any;
  private _sitemap: any;

  public constructor(public data) {
    this._linkedItems = data.linkedItems;
    this._sitemap = {};
    this._generateSitemap(data.item, this._linkedItems, []);
  }

  private _generateSitemapItem(item: any, content: any, urls: any) {
    let url;
    if (item.system.type == 'homepage') {
      url = [];
    } else if (!item.elements.url) {
      if (item.elements.category?.value[0].codename) url = item.elements.category.value[0].codename;
      else if (item.elements.tag?.value[0].codename) url = item.elements.tag.value[0].codename;
      else url = item.elements.service?.value[0].codename;
    } else {
      url = item.elements.url.value;
    }

    const slug = [...urls, url];

    this._sitemap[`${slug.join('/')}`] = {
      slug: `${slug.join('/')}`,
      codename: item.system.codename,
      type: item.system.type,
      excludeFromSitemap: item.elements.metadataExcludeFromSitemapXml?.value[0]?.codename == 'yes',
      lastModified: item.system.lastModified,
    };

    if (item.elements.subpages) {
      item.elements.subpages.value.map((subitem) => {
        this._generateSitemap(content[subitem], content, slug);
      });
    }
    return;
  }
  private _generateSitemap(homepage, items, urls) {
    this._generateSitemapItem(homepage, items, urls);
  }
  public getContentBySlug(slug) {
    return slug && this._linkedItems[this._sitemap[slug].codename];
  }
  public getSitemapXML(host) {
    let items = '';
    forEach(this._sitemap, (element) => {
      if (!element.excludeFromSitemap) {
        const date = new Date(element.lastModified);
        const displayDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
        items += `
            <url>
              <loc>${`${host}${element.slug}`}</loc>
              <lastmod>${displayDate}</lastmod>
            </url>
          `;
      }
    });
    return `
      <xml version="1.0" encoding="UTF-8">
      <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
       ${items}
      </urlset>
      </xml>
   `;
  }
}

export const getSitemapXML = (protocol: string, host: string, sitemapData: SitemapDataWithCampaigns | SitemapData) => {
  let items = '';
  //Remove logic after launching the full site
  if (process.env.ENABLE_REDIRECTS === 'false') {
    Object.keys(sitemapData).forEach((key) => {
      const date = new Date(sitemapData[key]).toISOString();
      // Until DNS is completly taken over, this is the hotfix for sitemap
      const finalHost = host.includes('rambollfoundation-prod') ? 'rambollfonden.com' : host;
      items += `
              <url>
                <loc>${`${protocol}${finalHost}${key == '/' ? '' : key}`}</loc>
                <lastmod>${date}</lastmod>
              </url>
            `;
    });
  } else {
    sitemapData.forEach((campaign) => {
      Object.keys(campaign.urls).forEach((key) => {
        const date = new Date(sitemapData[key]).toISOString();
        //TODO: REMOVE CAMPAIGN LOGIC BEFORE LAUNCHING FULL SITE (use ENTIRE key instead of newUrl)
        const finalHost = process.env.ENABLE_REDIRECTS === 'true' ? process.env.CAMPAIGN_PRELAUNCH_URL : host;
        const newUrl = `${campaign.campaignUrl}/${key.split('/')[key.split('/').length - 1]}`;
        items += `
                <url>
                  <loc>${`${finalHost}${newUrl == '/' ? '' : newUrl}`}</loc>
                  <lastmod>${date}</lastmod>
                </url>
              `;
      });
    });
  }

  return `
      <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
       ${items}
      </urlset>
   `;
};

export const apiElements = [
  'category',
  'url',
  'summary__title',
  'summary__description',
  'tags__sector',
  'tags__theme_topic',
  'tags__content_type',
  'part_of_campaign_page',
];

const byCampaign = async (locale: string, campaignCodename: string) => {
  const response = await ProdClient.items<ContentPage>()
    .type('content_page')
    .emptyFilter('elements.metadata__exclude_from_sitemap_xml')
    .containsFilter('elements.part_of_campaign_page', [campaignCodename])
    .elementsParameter(apiElements)
    .languageParameter(locale)
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise();
  return response.data.items;
};

const all = async (locale: string) => {
  const response = await ProdClient.items<ContentPage>()
    .type('content_page')
    .emptyFilter('elements.metadata__exclude_from_sitemap_xml')
    .notEmptyFilter('elements.url')
    .elementsParameter(apiElements)
    .languageParameter(locale)
    .equalsFilter('system.language', locale)
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise();
  return response.data.items;
};
const byCodename = async (locale: string, codename: string, fullModel = true) => {
  const depthParameter = codename === 'active_ownership' ? TEAM_DEPTH_PARAMETER : DEPTH_PARAMETER;
  const request = (depth: number, elements?: string[]) => {
    const query = ProdClient.item<ContentPage>(codename).depthParameter(depth).languageParameter(locale);
    return (elements ? query.elementsParameter(elements) : query).toPromise();
  };

  if (!fullModel) {
    return await request(depthParameter, apiElements)
      .then((response) => response.data.item)
      .catch(() => null);
  }

  const response = await request(depthParameter);
  if (depthParameter === DEPTH_PARAMETER && containsTeamModule(response.data.item)) {
    return (await request(TEAM_DEPTH_PARAMETER)).data.item;
  }
  return response.data.item;
};
const bySlug = async (locale: string, slug: string, fullModel = true) => {
  const page = await ProdClient.items<ContentPage>()
    .type('content_page')
    .languageParameter(locale)
    .equalsFilter('elements.url', slug)
    .limitParameter(1)
    .elementsParameter(['category'])
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise()
    .then((response) => response.data.items[0]);

  let response = {} as ContentPage | null;
  if (page?.elements.category.value[0]?.codename === 'custom_form_page') {
    response = await ProdClient.item<ContentPage>(page.system.codename)
      .languageParameter(locale)
      .depthParameter(7)
      .toPromise()
      .then((response) => response.data.item);
  } else response = page ? await byCodename(locale, page.system.codename, fullModel) : null;
  return response;
};
const byCategory = async (locale: string, category: string) => {
  const response = await ProdClient.items<ContentPage>()
    .type('content_page')
    .depthParameter(DEPTH_PARAMETER)
    .containsFilter('elements.category', [category])
    .languageParameter(locale)
    .equalsFilter('system.language', locale)
    .elementsParameter(apiElements)
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise();
  return response.data.items;
};

const allLocales = async (codename: string) => {
  const keyValues = await Promise.all(
    distinctLangaugeVariants.map(async (languageCodename) => {
      const translations = await getTranslations(languageCodename);
      const slug = await ProdClient.item<ContentPage>(codename)
        .languageParameter(languageCodename)
        .elementsParameter(apiElements)
        .toPromise()
        .then((response) => Object.keys(normalize(response.data.item, undefined, undefined, true, translations))[0])
        .catch(() => '');

      return [languageCodename, slug];
    }),
  );

  return keyValues.reduce((obj, [key, value]) => {
    obj[key] = value;
    return obj;
  }, {} as { [key: string]: string });
};

const byId = async (locale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE!, id: string) => {
  return await ProdClient.items<ContentPage>()
    .type('content_page')
    .limitParameter(1)
    .depthParameter(DEPTH_PARAMETER)
    .elementsParameter(apiElements)
    .languageParameter(locale)
    .equalsFilter('system.id', id)
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise()
    .then((res) => res.data.items[0]);
};
export const GetContentPage = {
  all,
  byCategory,
  byCodename,
  bySlug,
  byCampaign,
  allLocales,
  byId,
};

const model = async (locale: string) => {
  const response = await ProdClient.item<Homepage>('homepage').languageParameter(locale).depthParameter(3).toPromise();
  return response.data.item;
};

export const getHomepage = {
  model,
};
